import React, { useState } from 'react';
import { Box, Button, LinearProgress, Menu, MenuItem, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { ClientDashboardItem } from '@/types/client';
import { useLocation, useNavigate } from 'react-router-dom';
import addMealPlanIcon from '@/assets/images/add-meal-plan-icon.svg';
import scheduleIcon from '@/assets/images/schedule-icon.svg';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { getClientInvoiceStatusMeta } from './clientInvoiceStatus';
import {
  formatClientRestoreDeadline,
  getClientRecoverableUntil,
  isPendingDeletionClient,
} from '@/utils/client-account-deletion';

interface ClientListTableProps {
  clients: ClientDashboardItem[];
  onCardClick?: (clientId: string) => void;
  onOpenPanel?: (clientId: string) => void;
  onScheduleClient?: (client: ClientDashboardItem) => void;
  onSuspendClient?: (clientId: string) => void;
  /** Continue-setup click for a draft row (e.g. a draft mixed into the All Clients list). */
  onDraftClick?: (draftId: string) => void;
  onDeleteClient?: (client: ClientDashboardItem) => void;
  highlightedClientId?: string | null;
  footer?: React.ReactNode;
}

type RowStatus = 'active' | 'suspended' | 'draft' | 'pending_deletion';

const statusMeta: Record<RowStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: '#10b981' },
  suspended: { label: 'Suspended', color: '#ef4444' },
  draft: { label: 'Draft', color: '#64748b' },
  pending_deletion: { label: 'Pending deletion', color: '#d97706' },
};

const normalizeStatus = (status: string | undefined, isPendingDeletion = false): RowStatus => {
  if (isPendingDeletion || status === 'pending_deletion') return 'pending_deletion';
  if (status === 'draft') return 'draft';
  if (status === 'active') return 'active';
  if (status === 'suspended' || status === 'inactive' || status === 'archived') return 'suspended';
  return 'active';
};

const getClientsListStatusParam = (searchParams: URLSearchParams): string | null => {
  const status = searchParams.get('status');
  return status === 'active' || status === 'suspended' || status === 'draft' || status === 'pending_deletion' ? status : null;
};

const formatSessionDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SendPaymentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.5"
      d="M15.9665 4.55812L14.1369 2.72848L14.1369 2.72848C12.5913 1.18295 11.8186 0.410183 10.816 0.122639C9.81336 -0.164906 8.74849 0.0808339 6.61875 0.572313L5.39057 0.85574C3.5988 1.26922 2.70292 1.47597 2.08944 2.08944C1.47597 2.70292 1.26922 3.59881 0.855739 5.39057L0.572313 6.61875C0.0808338 8.74849 -0.164906 9.81336 0.122639 10.816C0.410183 11.8186 1.18295 12.5913 2.72848 14.1369L4.55812 15.9665L4.55813 15.9665C7.24711 18.6555 8.5916 20 10.2623 20C11.933 20 13.2775 18.6555 15.9665 15.9665L15.9665 15.9665C18.6555 13.2775 20 11.933 20 10.2623C20 8.5916 18.6555 7.24711 15.9665 4.55813L15.9665 4.55812Z"
      fill="#20AEDC"
    />
  </svg>
);

const SuspendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 10C14.2091 10 16 8.20914 16 6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6C8 8.20914 9.79086 10 12 10Z" fill="#FF8585" />
    <path fillRule="evenodd" clipRule="evenodd" d="M16.5 15.75C14.9812 15.75 13.75 16.9812 13.75 18.5C13.75 19.0004 13.8832 19.4691 14.1167 19.8732L17.8732 16.1167C17.4691 15.8832 17.0004 15.75 16.5 15.75ZM18.9196 17.1916L15.1916 20.9196C15.5806 21.1305 16.0261 21.25 16.5 21.25C18.0188 21.25 19.25 20.0188 19.25 18.5C19.25 18.0261 19.1305 17.5806 18.9196 17.1916ZM12.25 18.5C12.25 16.1528 14.1528 14.25 16.5 14.25C17.689 14.25 18.7652 14.7393 19.5357 15.5256C20.2861 16.2914 20.75 17.3423 20.75 18.5C20.75 20.8472 18.8472 22.75 16.5 22.75C15.3423 22.75 14.2914 22.2861 13.5256 21.5357C12.7393 20.7652 12.25 19.689 12.25 18.5Z" fill="#FF8585" />
    <path opacity="0.4" d="M17.9963 14.5211C17.5308 14.3459 17.0265 14.25 16.5 14.25C14.1528 14.25 12.25 16.1528 12.25 18.5C12.25 19.689 12.7393 20.7652 13.5256 21.5357C13.6786 21.6856 13.8429 21.8241 14.0173 21.9497C13.4088 21.9828 12.7385 22 12 22C4 22 4 19.9853 4 17.5C4 15.0147 7.58172 13 12 13C14.3874 13 16.5305 13.5882 17.9963 14.5211Z" fill="#FF8585" />
  </svg>
);

const ViewIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 3H3C2.44772 3 2 3.44772 2 4V7C2 7.55228 2.44772 8 3 8H21C21.5523 8 22 7.55228 22 7V4C22 3.44772 21.5523 3 21 3Z" stroke="#374A66" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 8V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V8" stroke="#374A66" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 12H14" stroke="#374A66" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ClientListTable: React.FC<ClientListTableProps> = ({
  clients,
  onCardClick,
  onOpenPanel,
  onScheduleClient,
  onSuspendClient,
  onDraftClick,
  onDeleteClient,
  highlightedClientId = null,
  footer,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuClient, setMenuClient] = useState<ClientDashboardItem | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const page = searchParams.get('page') || '1';
  const limit = searchParams.get('limit') || '10';
  const status = getClientsListStatusParam(searchParams);
  const view = searchParams.get('view') ?? searchParams.get('fromView');
  const fromSuffix = `${status ? `&fromStatus=${status}` : ''}${view === 'list' ? '&fromView=list' : ''}`;

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuClient(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, client: ClientDashboardItem) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuClient(client);
  };

  const currentMenuStatus = normalizeStatus(menuClient?.status, isPendingDeletionClient(menuClient));

  return (
    <>
      <Box sx={{ borderRadius: '10px', border: '1px solid #e3e8ee', overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              minWidth: 940,
              display: 'grid',
              gridTemplateColumns: '1.5fr 0.9fr 0.9fr 1.1fr 0.9fr 0.9fr',
              alignItems: 'center',
              borderBottom: '1px solid #edf1f5',
              bgcolor: '#f7f9fb',
              px: 1,
              py: 1,
            }}
          >
            {['NAME', 'LAST SESSION', 'UPCOMING SESSION', 'QUESTIONNAIRE PROGRESS', 'INVOICE STATUS', 'ACTIONS'].map((h) => (
              <Typography key={h} sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {clients.map((client) => {
            const rowStatus = normalizeStatus(client.status, isPendingDeletionClient(client));
            const status = statusMeta[rowStatus];
            const isDraft = rowStatus === 'draft';
            const qProgress = Math.max(0, Math.min(100, client.questionnaireProgress ?? 0));
            const invoiceMeta = getClientInvoiceStatusMeta(client.invoiceStatus);
            const restoreUntil = formatClientRestoreDeadline(getClientRecoverableUntil(client));
            const isHighlighted = highlightedClientId === client.id;

            return (
              <Box
                key={client.id}
                id={`client-card-${client.id}`}
                data-client-id={client.id}
                sx={{
                  minWidth: 940,
                  display: 'grid',
                  gridTemplateColumns: '1.5fr 0.9fr 0.9fr 1.1fr 0.9fr 0.9fr',
                  alignItems: 'center',
                  px: 1,
                  py: 1.1,
                  borderBottom: '1px solid #f1f4f7',
                  bgcolor: isHighlighted ? '#fff7ed' : rowStatus === 'pending_deletion' ? '#f8fafc' : undefined,
                  boxShadow: isHighlighted ? 'inset 3px 0 0 #d97706' : undefined,
                }}
              >
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', fontWeight: 500, color: '#334155' }}>
                    {isDraft ? (
                      client.fullName || 'Untitled draft'
                    ) : (
                      <ClientNameLink clientId={client.id} className="text-[#334155] hover:text-[#334155]">
                        {client.fullName || 'Unknown'}
                      </ClientNameLink>
                    )}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: status.color }}>
                    • {status.label}
                  </Typography>
                  {rowStatus === 'pending_deletion' ? (
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#b45309', mt: 0.25 }}>
                      Deleted in companion app
                      {restoreUntil ? ` · Restore until ${restoreUntil}` : ''}
                    </Typography>
                  ) : null}
                </Box>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', color: '#64748b' }}>
                  {formatSessionDate(client.lastSessionDate)}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', color: '#64748b' }}>
                  {formatSessionDate(client.upcomingSessionDate)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#1e293b', minWidth: 30 }}>
                    {qProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={qProgress}
                    sx={{
                      width: 96,
                      height: 8.4,
                      borderRadius: '999px',
                      bgcolor: '#e5e7eb',
                      '& .MuiLinearProgress-bar': { borderRadius: '999px', bgcolor: '#25a8da' },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8 }}>
                  {invoiceMeta.showIcon ? (
                    <Box
                      sx={{
                        width: 19.2,
                        height: 19.2,
                        borderRadius: '7px',
                        bgcolor: invoiceMeta.iconBg,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {invoiceMeta.tone === 'overdue' ? (
                        <CloseRoundedIcon sx={{ fontSize: 14.4, color: '#fff', strokeWidth: 1.5 }} />
                      ) : (
                        <CheckRoundedIcon sx={{ fontSize: 14.4, color: '#fff', strokeWidth: 1.5 }} />
                      )}
                    </Box>
                  ) : null}
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', color: invoiceMeta.color }}>
                    {invoiceMeta.label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    onClick={() => {
                      if (isDraft) {
                        onDraftClick?.(client.id);
                        return;
                      }
                      onCardClick?.(client.id);
                    }}
                    variant="outlined"
                    startIcon={<RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#d1d5db',
                      color: '#20a8d7',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.86rem',
                      borderRadius: '6px',
                      minWidth: 112,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Profile
                  </Button>
                  <Box
                    onClick={(event) => openMenu(event, client)}
                    sx={{ color: '#111827', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <MoreVertRoundedIcon sx={{ fontSize: 20 }} />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {footer}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: '4px',
            p: '4px 8px',
            width: 166,
            boxShadow: '0 8px 24px rgba(16, 24, 40, 0.14)',
          },
        }}
      >
        {currentMenuStatus !== 'pending_deletion' ? (
          <>
        <MenuItem
          onClick={() => {
            if (!menuClient) return;
            navigate(`/clients/${menuClient.id}/details-v2/invoices?fromPage=${page}&fromLimit=${limit}${fromSuffix}`);
            closeMenu();
          }}
          sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
        >
          <SendPaymentIcon />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: 'rgba(77,89,109,0.8)' }}>
            Send Payment
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuClient) return;
            navigate(`/clients/${menuClient.id}/details-v2/meal-plan?fromPage=${page}&fromLimit=${limit}${fromSuffix}`);
            closeMenu();
          }}
          sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
        >
          <Box component="img" src={addMealPlanIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: 'rgba(77,89,109,0.8)' }}>
            Add Meal Plan
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuClient) return;
            onScheduleClient?.(menuClient);
            closeMenu();
          }}
          sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
        >
          <Box component="img" src={scheduleIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: 'rgba(77,89,109,0.8)' }}>
            Schedule
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuClient) return;
            if (currentMenuStatus === 'active') onSuspendClient?.(menuClient.id);
            closeMenu();
          }}
          disabled={currentMenuStatus !== 'active'}
          sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
        >
          <SuspendIcon />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: 'rgba(77,89,109,0.8)' }}>
            Suspend
          </Typography>
        </MenuItem>
          </>
        ) : null}
        {onDeleteClient && currentMenuStatus !== 'pending_deletion' && currentMenuStatus !== 'draft' ? (
          <MenuItem
            onClick={() => {
              if (!menuClient) return;
              onDeleteClient(menuClient);
              closeMenu();
            }}
            sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: '#b42318' }}>
              Delete client
            </Typography>
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (!menuClient) return;
            onOpenPanel?.(menuClient.id);
            closeMenu();
          }}
          sx={{ minHeight: 30, px: 0, gap: 1, borderRadius: '4px' }}
        >
          <ViewIcon />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500, color: 'rgba(77,89,109,0.8)' }}>
            View
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
